import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button, Notification } from '@telus-uds/components-web';

import IotTableList from '../iotTableList';
import { buildColumnDescriptors, COLUMN_KEYS } from '../iotTableList/util';
import { IotTermMTM } from '../../../utils/constant';
import {
  Backdrop,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  TableBlock
} from './css';

/**
 * Resolve a named table to its list of `ioTTableListItem` entries from the
 * preloaded Contentful payload. Each entry in `iotPopupTablesName` is a direct
 * field key on `contentfulData` (the same convention the other IoT sections
 * use, e.g. `rateplanList`, `hardwareList`). Returns null when no matching
 * array field exists so the caller can show a non-blocking empty state.
 */
export const resolveTable = (contentfulData, name) => {
  if (!contentfulData || !name) return null;

  const table = _.get(contentfulData, name);
  return Array.isArray(table) ? table : null;
};

/**
 * Derive the auto-populated unit counts a hardware row inherits from the rate
 * plans completed elsewhere in the same popup. A hardware row is linked to its
 * plan by a shared `rateplanType`, so the linkage is authored in Contentful
 * rather than inferred from product names.
 *
 * Month-to-month plan units become `mtmFloor` (chargeable, and the minimum the
 * rep may enter); committed-term units become `ratePlanUnits` (included, not
 * charged). Floors are derived on every render and never persisted — only the
 * rep's surplus above the floor is stored on the product.
 *
 * @returns {Object<string, { ratePlanUnits: number, mtmFloor: number }>} keyed by row id
 */
export const buildUnitFloors = (tableNames, contentfulData, products) => {
  const mtmByType = {};
  const committedByType = {};

  (Array.isArray(products) ? products : []).forEach(product => {
    const type = _.get(product, 'rateplanType', '');
    const term = _.get(product, 'term', '');
    if (!type || !term) return;
    const quantity = Number(_.get(product, 'quantity', 0)) || 0;
    if (!quantity) return;
    const bucket = term === IotTermMTM ? mtmByType : committedByType;
    bucket[type] = (bucket[type] || 0) + quantity;
  });

  const floors = {};

  (Array.isArray(tableNames) ? tableNames : []).forEach(name => {
    const table = resolveTable(contentfulData, name);
    if (!table) return;

    const headerEntry = table.find(entry =>
      _.get(entry, 'fields.isHeader', false)
    );
    const isHardwareTable = buildColumnDescriptors(headerEntry).some(
      column => _.get(column, 'key') === COLUMN_KEYS.MTM_UNITS
    );
    if (!isHardwareTable) return;

    table.forEach(entry => {
      if (_.get(entry, 'fields.isHeader', false)) return;
      const type = _.get(entry, 'fields.rateplanType', '');
      const rowKey = _.get(entry, 'sys.id', '');
      if (!type || !rowKey) return;
      const mtmFloor = mtmByType[type] || 0;
      const ratePlanUnits = committedByType[type] || 0;
      if (!mtmFloor && !ratePlanUnits) return;
      floors[rowKey] = { ratePlanUnits, mtmFloor };
    });
  });

  return floors;
};

/**
 * Accessible popup that displays one or more preloaded Contentful tables named
 * in `tableNames`, each rendered as an interactive `IotTableList`. When a rep
 * completes a row (all required fields selected) it is merged into
 * `completedProducts` via `onProductsChange`; withdrawing a row removes it.
 * The dialog stays open after each add and closes only on explicit dismissal.
 * A name with no matching data shows a non-blocking empty state without
 * failing the rest.
 *
 * Note: a UDS `Modal` only supports plain `bodyText`, so — consistent
 * with the existing rich-content popup (couponPopup) — this uses a custom
 * accessible dialog to host the tables.
 */
const IotPopupTables = ({
  isOpen,
  onClose,
  heading,
  tableNames,
  contentfulData,
  emptyTableMessage,
  doneLabel,
  completedProducts,
  onProductsChange,
  focusElementAfterClose,
  isRenewal
}) => {
  const containerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Draft of completed products held while the popup is open. Rows update this
  // draft as they are completed/withdrawn, but nothing is committed to the
  // order (the field's `completedProducts`) until the rep clicks Done.
  const [draftProducts, setDraftProducts] = useState(completedProducts);

  // Keep the draft in sync with the committed products whenever the popup is
  // closed — this covers both cancelling (discarding uncommitted edits) and
  // external changes such as removing a product from the summary below the
  // button while the popup is closed. While the popup is open the draft is left
  // alone so an in-progress edit is never clobbered. Syncing while closed (rows
  // unmounted) guarantees the next open seeds rows from the correct state.
  useEffect(() => {
    if (!isOpen) setDraftProducts(completedProducts);
  }, [isOpen, completedProducts]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    if (containerRef.current) containerRef.current.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (focusElementAfterClose && focusElementAfterClose.current) {
        focusElementAfterClose.current.focus();
      }
    };
    // Run only on open/close transitions. `onClose` is read through a ref so a
    // new callback identity on each parent re-render (e.g. when a completed
    // product updates) does not re-run this effect and re-focus the dialog,
    // which would steal focus from an input mid-typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const names = Array.isArray(tableNames) ? tableNames : [];
  const unitFloors = buildUnitFloors(names, contentfulData, draftProducts);

  const handleRowComplete = (rowKey, product) => {
    setDraftProducts(prev => {
      const others = prev.filter(entry => entry.key !== rowKey);
      return product ? [...others, product] : others;
    });
  };

  const handleDone = () => {
    onProductsChange(draftProducts);
    onClose();
  };

  return ReactDOM.createPortal(
    <Backdrop onClick={onClose}>
      <ModalContainer
        role="dialog"
        aria-modal="true"
        aria-label={heading}
        tabIndex={-1}
        ref={containerRef}
        onClick={event => event.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>{heading}</ModalTitle>
          <CloseButton type="button" aria-label="Close" onClick={onClose}>
            &times;
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {names.map(name => {
            const tableList = resolveTable(contentfulData, name);
            const hasData = Array.isArray(tableList) && tableList.length > 0;
            const headerEntry =
              hasData &&
              tableList.find(entry => _.get(entry, 'fields.isHeader', false));
            const tableTitle = _.get(
              headerEntry,
              'fields.headerList[0].fields.title',
              name
            );
            return (
              <TableBlock key={name}>
                {hasData ? (
                  <IotTableList
                    tableList={tableList}
                    interactive
                    completedProducts={draftProducts}
                    unitFloors={unitFloors}
                    isRenewal={isRenewal}
                    onRowComplete={(rowKey, product) =>
                      handleRowComplete(
                        rowKey,
                        product
                          ? {
                              ...product,
                              sourceTable: name,
                              sourceTableTitle: tableTitle
                            }
                          : null
                      )
                    }
                  />
                ) : (
                  <Notification variant={{ style: 'subtle' }}>{emptyTableMessage}</Notification>
                )}
              </TableBlock>
            );
          })}
        </ModalBody>
        <ModalFooter>
          <Button variant={{ priority: 'high' }} onPress={handleDone}>
            {doneLabel}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Backdrop>,
    document.body
  );
};

IotPopupTables.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  heading: PropTypes.string,
  tableNames: PropTypes.arrayOf(PropTypes.string),
  contentfulData: PropTypes.shape({}),
  emptyTableMessage: PropTypes.string,
  doneLabel: PropTypes.string,
  completedProducts: PropTypes.arrayOf(PropTypes.shape({})),
  onProductsChange: PropTypes.func,
  focusElementAfterClose: PropTypes.shape({
    current: PropTypes.instanceOf(Object)
  }),
  isRenewal: PropTypes.bool
};

IotPopupTables.defaultProps = {
  heading: '',
  tableNames: [],
  contentfulData: null,
  emptyTableMessage: '',
  doneLabel: 'Done',
  completedProducts: [],
  onProductsChange: () => {},
  focusElementAfterClose: null,
  isRenewal: false
};

export default IotPopupTables;
